import type {PaidPlanId} from "pricing/planCatalogue";

/**
 * The send screen's slide-out card family: which one is out, and where each sits (ui#2468, ui#2278).
 *
 * Three cards slide out from behind the send panel (the limit-wall email's offer, the download-limit
 * warning and the upsell ask), and they share one piece of state because the rule that matters is
 * about the FAMILY: at most one is out at a time, and opening one tucks the others. Holding a flag per
 * card instead let two slide out over each other the moment a sender near their cap also reached for a
 * locked control.
 *
 * Pure domain, no React (epic #26). The state lives in the panel that owns the cards, never in a card:
 * the cards are portalled into a host the peel frame hands down, and any churn of that host remounts
 * them. With this bookkeeping inside a card, a remount re-armed its self-opening and a card the reader
 * had just tucked slid back out on its own (found by the local-stack journey, ui#2468).
 */

/**
 * The cards in stack order: downwards from the anchor on a wide screen, and left to right in the
 * tucked chips' row on a phone. The limit-wall offer leads (kitecorp/transferify-ui#3197): the sender
 * followed its email here, so it is what they came for. The warning comes next because it is about a
 * cap the sender is already spending; the upsell is the softer ask after it.
 */
export const SLIDE_OUT_CARD_ORDER = ["offer", "limit", "upsell"] as const;

export type SlideOutCardId = typeof SLIDE_OUT_CARD_ORDER[number];

export interface SlideOutCardsState {
	/** The ONE card that is out, or null while both are tucked. */
	open: SlideOutCardId | null;
	/** The cards whose one self-opening has been spent, whatever spent it. */
	spent: readonly SlideOutCardId[];
}

export const SLIDE_OUT_CARDS_TUCKED: SlideOutCardsState = {open: null, spent: []};

/** The gap between two stacked cards, between an open card and the panel's edge, and between two chips in a row. */
export const SLIDE_OUT_GAP_PX = 12;
/**
 * The tucked card: its own round glyph chip and nothing else (owner 2026-09-16). 44px is both the
 * chip's diameter and the minimum comfortable touch target, so the tucked card needs no hit area
 * larger than what it draws.
 */
export const SLIDE_OUT_CHIP_PX = 44;

/**
 * Whether any card of the family is out (ui#2949). The trial-expiry offer also stands to the panel's
 * LEFT, beside the file row, so while a card is out there the offer steps aside rather than the two
 * drawing over each other; it returns when the card tucks.
 */
export function slideOutCardIsOut(cards: Record<SlideOutCardId, {open: boolean}>): boolean {
	return SLIDE_OUT_CARD_ORDER.some(id => cards[id].open);
}

export function slideOutIsOpen(state: SlideOutCardsState, id: SlideOutCardId): boolean {
	return state.open === id;
}

/** A card of the family beside an active limit-wall offer: whether it has something to say, and the plan it sells. */
export interface SlideOutNeighbour {
	due: boolean;
	/** The plan the card's one call to action sells, or null for a card that names none. */
	plan: PaidPlanId | null;
}

export interface SlideOutCardsBesideOffer {
	limit: boolean;
	upsell: boolean;
	/** The reason line of a dropped limit card, which the offer's own line then leads with; null otherwise. */
	offerLead: string | null;
}

/**
 * Which of the other two cards still stack while a limit-wall offer is active, and what the offer takes
 * over from a card it replaces (owner 2026-10-03, kitecorp/transferify-ui#3197).
 *
 * <b>No second card for the same plan.</b> A card that pitches the offer's own plan is not shown while
 * the offer is: the download-limit card's "Upgrade to Basic", or an upsell card for a control Basic
 * unlocks, would be the same ask at a worse price beside the discounted one. The offer card takes its
 * place, and if the dropped card was the limit warning its reason line is handed over, so the usage the
 * sender was about to be warned of still reaches them, on the offer's one line. A card for a DIFFERENT
 * plan still stacks under the offer; it is a different ask. With no offer active, nothing changes.
 */
export function slideOutCardsBesideOffer(offerPlan: PaidPlanId | null, limit: SlideOutNeighbour & {reason: string | null},
	upsell: SlideOutNeighbour): SlideOutCardsBesideOffer {
	const samePlan = (card: SlideOutNeighbour) => offerPlan !== null && card.plan === offerPlan;
	const limitReplaced = limit.due && samePlan(limit);
	return {
		limit: limit.due && !limitReplaced,
		upsell: upsell.due && !samePlan(upsell),
		offerLead: limitReplaced ? limit.reason : null,
	};
}

function spend(state: SlideOutCardsState, id: SlideOutCardId): readonly SlideOutCardId[] {
	return state.spent.includes(id) ? state.spent : [...state.spent, id];
}

/**
 * The one self-opening on first eligibility.
 *
 * A no-op once spent, and a no-op, WITHOUT spending it, while another card is out: the offer card
 * opens first, then the limit card, and the upsell never slides out over either. The self-opening is deliberately held rather
 * than discarded, so the ask the reader never saw is still owed once they tuck what is covering it;
 * a discarded one would be recorded as shown to a sender who was never shown anything.
 */
export function slideOutSelfOpen(state: SlideOutCardsState, id: SlideOutCardId): SlideOutCardsState {
	if (state.spent.includes(id) || state.open !== null) {
		return state;
	}
	return {open: id, spent: spend(state, id)};
}

/** The reader's click on a tucked chip: it opens, the other tucks, and its self-opening is spent too. */
export function slideOutOpen(state: SlideOutCardsState, id: SlideOutCardId): SlideOutCardsState {
	return {open: id, spent: spend(state, id)};
}

/**
 * The reader's ×. The self-opening counts as spent even if it never fired, so nothing but another
 * click can bring this card back — not a remount, not a recipient change, not a usage refresh.
 */
export function slideOutTuck(state: SlideOutCardsState, id: SlideOutCardId): SlideOutCardsState {
	return {open: state.open === id ? null : state.open, spent: spend(state, id)};
}

/**
 * A card that is OUT stops having anything to say: the limit-wall offer once this visit paid, the limit
 * warning when the offer for the same plan arrives and takes its place (kitecorp/transferify-ui#3197).
 * The card leaves the stack, so nothing is out any more, and the next card owed a self-opening gets its
 * turn. Its own self-opening stays spent: it was shown. A card that is not the open one is left alone,
 * so a remount or a refresh of another card's eligibility cannot tuck what the reader is looking at.
 */
export function slideOutWithdraw(state: SlideOutCardsState, id: SlideOutCardId): SlideOutCardsState {
	return state.open === id ? {open: null, spent: state.spent} : state;
}

/** One card in the stack: whether it is out, and the height it takes when it is. */
export interface SlideOutCardBox {
	id: SlideOutCardId;
	open: boolean;
	/** The card's measured open height. Ignored while it is tucked, which is chip-sized. */
	height: number;
}

/**
 * Each visible card's top offset from the anchor, in the order given: the wide layout, where the stack
 * stands to the panel's left.
 *
 * The first card sits AT the anchor (the file row) and every later one below what precedes it, so a
 * tucked chip never lands on the open card beside it. A tucked card contributes its chip's height
 * rather than the card's, which is why the substitution lives here and not in the measuring layer:
 * the box the reader sees is the chip, and the stack has to be spaced by what is drawn.
 */
export function slideOutCardTops(boxes: readonly SlideOutCardBox[]): number[] {
	let top = 0;
	return boxes.map(box => {
		const at = top;
		top += (box.open ? box.height : SLIDE_OUT_CHIP_PX) + SLIDE_OUT_GAP_PX;
		return at;
	});
}

/** Where a card sits in the stacked layout, under the panel (a phone, or any width below side by side). */
export interface SlideOutStackedSpot {
	/** The card's top edge, measured down from the panel's bottom edge. */
	top: number;
	/**
	 * A tucked chip's left edge, measured from the panel's centre line. Null for the open card, which
	 * hangs across the panel's width rather than at a point on it.
	 */
	left: number | null;
}

/**
 * Each visible card's place under the panel in the stacked layout, in the order given (ui#3227).
 *
 * The open card hangs first, one gap under the panel's bottom edge. Every tucked chip then sits in ONE
 * row under it: one gap under the open card, or right on the panel's edge when nothing is open, which
 * is where the wide layout's chips sit too (touching the panel's side). The row keeps the stack order
 * left to right and is centred on the panel as a group, its chips one gap apart. Before this, each chip
 * centred itself under the panel, so two tucked chips were drawn one on top of the other.
 *
 * Offsets are from the panel's centre line rather than its left edge so this needs no panel width: the
 * styles add them to the centre, which keeps the rule here and the measuring out of it.
 */
export function slideOutStackedSpots(boxes: readonly SlideOutCardBox[]): SlideOutStackedSpot[] {
	let below = 0;
	const openTops = boxes.map(box => {
		if (!box.open) return null;
		const at = below + SLIDE_OUT_GAP_PX;
		below = at + box.height;
		return at;
	});
	const rowTop = below === 0 ? 0 : below + SLIDE_OUT_GAP_PX;
	const chips = openTops.filter(top => top === null).length;
	let left = -(chips * SLIDE_OUT_CHIP_PX + (chips - 1) * SLIDE_OUT_GAP_PX) / 2;
	return openTops.map(openTop => {
		if (openTop !== null) return {top: openTop, left: null};
		const at = left;
		left += SLIDE_OUT_CHIP_PX + SLIDE_OUT_GAP_PX;
		return {top: rowTop, left: at};
	});
}
